import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { sendReimbursementSubmittedEmail } from "@/lib/notificationEmails";
import { invalidateInventoryQueries } from "@/lib/mutationHelpers";

export type InvoiceFrequency = "per_cleaning" | "weekly" | "biweekly" | "monthly";

export interface PropertyBillingSettings {
  property_id: string;
  billing_mode: "solo_cleaner" | "company" | "track_only";
  default_bill_to_type: "owner_to_cleaner" | "owner_to_company" | "none";
  default_bill_to_id: string | null;
  inventory_autopay_enabled: boolean;
  /** How often to generate invoices: per cleaning, bi-weekly, or monthly (company or combined). */
  invoice_frequency: InvoiceFrequency;
  /** When true, owner can pay via Stripe in Stravo; when false, pay off-app (invoice for record-keeping). */
  pay_via_app: boolean;
  /** Retained for future; v1 behavior is always owner covers fees. */
  processing_fee_payer?: "owner" | "platform";
  /** v1 tax: cleaning-only percent */
  cleaning_tax_enabled?: boolean;
  cleaning_tax_rate_percent?: number | null;
  cleaning_tax_label?: string | null;
}

export interface InventoryReimbursement {
  id: string;
  property_id: string;
  cleaning_task_id: string | null;
  created_by_user_id: string;
  submitted_by_role: string;
  bill_to_type: string;
  bill_to_id: string | null;
  status: string;
  amount_cents: number;
  currency: string;
  receipt_urls: string[];
  description: string | null;
  paid_at: string | null;
  paid_method: string | null;
  invoice_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Fetch property billing settings (v2). Falls back to null if table not present. */
export function usePropertyBillingSettings(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["property-billing-settings", propertyId],
    queryFn: async (): Promise<PropertyBillingSettings | null> => {
      if (!propertyId) return null;
      const { data, error } = await supabase
        .from("property_billing_settings")
        .select("property_id, billing_mode, default_bill_to_type, default_bill_to_id, inventory_autopay_enabled, invoice_frequency, pay_via_app, processing_fee_payer, cleaning_tax_enabled, cleaning_tax_rate_percent, cleaning_tax_label")
        .eq("property_id", propertyId)
        .maybeSingle();
      if (error) {
        if (error.code === "42P01") return null; // table does not exist
        throw error;
      }
      const row = data as Record<string, unknown> | null;
      if (!row) return null;
      return {
        ...row,
        invoice_frequency: (row.invoice_frequency as InvoiceFrequency) ?? "monthly",
        pay_via_app: row.pay_via_app !== false,
      } as PropertyBillingSettings;
    },
    enabled: !!propertyId,
  });
}

/** Update inventory_autopay_enabled in property_billing_settings (upsert). */
export function useUpdatePropertyBillingAutopay(propertyId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (inventoryAutopayEnabled: boolean) => {
      if (!propertyId) throw new Error("Property ID required");
      const { error } = await supabase
        .from("property_billing_settings")
        .upsert(
          { property_id: propertyId, inventory_autopay_enabled: inventoryAutopayEnabled },
          { onConflict: "property_id" }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-billing-settings", propertyId] });
    },
  });
}

/** Update invoice_frequency and/or pay_via_app in property_billing_settings (upsert). */
export function useUpdatePropertyBillingPreferences(propertyId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      invoice_frequency?: InvoiceFrequency;
      pay_via_app?: boolean;
      cleaning_tax_enabled?: boolean;
      cleaning_tax_rate_percent?: number | null;
      cleaning_tax_label?: string | null;
    }) => {
      if (!propertyId) throw new Error("Property ID required");
      const update: Record<string, unknown> = { property_id: propertyId };
      if (payload.invoice_frequency !== undefined) update.invoice_frequency = payload.invoice_frequency;
      if (payload.pay_via_app !== undefined) update.pay_via_app = payload.pay_via_app;
      if (payload.cleaning_tax_enabled !== undefined) update.cleaning_tax_enabled = payload.cleaning_tax_enabled;
      if (payload.cleaning_tax_rate_percent !== undefined) update.cleaning_tax_rate_percent = payload.cleaning_tax_rate_percent;
      if (payload.cleaning_tax_label !== undefined) update.cleaning_tax_label = payload.cleaning_tax_label;
      const { error } = await supabase
        .from("property_billing_settings")
        .upsert(update, { onConflict: "property_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-billing-settings", propertyId] });
    },
  });
}

/** Reimbursements for a single cleaning task (cleaner "Purchases for this turnover"). */
export function useReimbursementsByTask(cleaningTaskId: string | undefined) {
  return useQuery({
    queryKey: ["inventory-reimbursements-by-task", cleaningTaskId],
    queryFn: async () => {
      if (!cleaningTaskId) return [];
      const { data, error } = await supabase
        .from("inventory_reimbursements")
        .select("*")
        .eq("cleaning_task_id", cleaningTaskId)
        .order("created_at", { ascending: false });
      if (error) {
        if (error.code === "42P01") return []; // table does not exist
        throw error;
      }
      return (data ?? []) as InventoryReimbursement[];
    },
    enabled: !!cleaningTaskId,
  });
}

/** All reimbursements for a property (owner/team admin Reimbursements sub-tab). */
export function usePropertyReimbursements(propertyId: string | undefined) {
  return useQuery({
    queryKey: ["property-reimbursements", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("inventory_reimbursements")
        .select("*")
        .eq("property_id", propertyId)
        .order("created_at", { ascending: false });
      if (error) {
        if (error.code === "42P01") return []; // table does not exist
        throw error;
      }
      return (data ?? []) as InventoryReimbursement[];
    },
    enabled: !!propertyId,
  });
}

export type CreateReimbursementInput = {
  propertyId: string;
  cleaningTaskId: string | null;
  createdByUserId: string;
  submittedByRole: "cleaner" | "company_admin" | "owner";
  billToType: "owner_to_cleaner" | "owner_to_company" | "none";
  billToId: string | null;
  amountCents: number;
  description?: string | null;
  receiptUrls: string[];
  paidImmediately: boolean;
  /** When set with quantity, increments that item's Ordered (not arrived). */
  inventoryItemId?: string | null;
  /** Quantity purchased (required when logging for inventory); used to increment ordered_qty. */
  quantity?: number;
};

export function useCreateInventoryReimbursement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateReimbursementInput) => {
      const now = new Date().toISOString();
      const qty = Math.max(1, Math.floor(input.quantity ?? 1));
      const row = {
        property_id: input.propertyId,
        cleaning_task_id: input.cleaningTaskId,
        created_by_user_id: input.createdByUserId,
        submitted_by_role: input.submittedByRole,
        bill_to_type: input.billToType,
        bill_to_id: input.billToId,
        status: input.paidImmediately ? "paid" : "submitted",
        amount_cents: input.amountCents,
        currency: "USD",
        receipt_urls: input.receiptUrls,
        description: input.description || null,
        paid_at: input.paidImmediately ? now : null,
        paid_method: input.paidImmediately ? "autopay" : null,
        inventory_item_id: input.inventoryItemId ?? null,
        quantity: qty,
      };
      const { data, error } = await supabase
        .from("inventory_reimbursements")
        .insert(row)
        .select()
        .single();
      if (error) throw error;
      const reimbursement = data as InventoryReimbursement & { inventory_item_id?: string | null; quantity?: number };
      if (input.inventoryItemId && qty >= 1) {
        const { data: item } = await supabase
          .from("inventory_items")
          .select("ordered_qty")
          .eq("id", input.inventoryItemId)
          .eq("property_id", input.propertyId)
          .single();
        const current = (item as { ordered_qty?: number } | null)?.ordered_qty ?? 0;
        await supabase
          .from("inventory_items")
          .update({ ordered_qty: current + qty })
          .eq("id", input.inventoryItemId)
          .eq("property_id", input.propertyId);
      }
      return reimbursement;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["inventory-reimbursements-by-task", variables.cleaningTaskId] });
      queryClient.invalidateQueries({ queryKey: ["property-reimbursements", variables.propertyId] });
      if (variables.inventoryItemId) {
        invalidateInventoryQueries(queryClient, variables.propertyId);
        queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
        queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
      }
      if (data?.status === "submitted") {
        sendReimbursementSubmittedEmail({
          propertyId: data.property_id,
          reimbursementId: data.id,
        }).catch(() => {});
      }
    },
  });
}

export function useApproveInventoryReimbursement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reimbursementId: string) => {
      const { error } = await supabase
        .from("inventory_reimbursements")
        .update({ status: "approved", updated_at: new Date().toISOString() })
        .eq("id", reimbursementId)
        .eq("status", "submitted");
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-reimbursements"] });
      queryClient.invalidateQueries({ queryKey: ["inventory-reimbursements-by-task"] });
    },
  });
}

export function useRejectInventoryReimbursement() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reimbursementId: string) => {
      const { error } = await supabase
        .from("inventory_reimbursements")
        .update({ status: "rejected", updated_at: new Date().toISOString() })
        .eq("id", reimbursementId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["property-reimbursements"] });
      queryClient.invalidateQueries({ queryKey: ["inventory-reimbursements-by-task"] });
    },
  });
}
